import React, { useState, useEffect } from 'react';
import PageHeader from '../components/Common/PageHeader';
import SlideoutPanel from '../components/Common/SlideoutPanel';
import { useSlideout } from '../hooks/useSlideout';

const RolesPermissionsView = ({ mockData, setMockData, showNotification }) => {
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [roleAssignments, setRoleAssignments] = useState([]);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedView, setSelectedView] = useState('roles'); // 'roles', 'assignments', 'templates'
  const [auditLog, setAuditLog] = useState([]);

  // Use shared slideout hook
  const { isOpen, title, content, width, openSlideout, closeSlideout } = useSlideout();

  // Complete permission categories and permissions
  const permissionCategories = {
    dashboard: {
      label: 'Dashboard',
      description: 'Main dashboard and analytics access',
      icon: '📊',
      permissions: [
        { id: 'dashboard.view', label: 'View Dashboard', description: 'Access to main dashboard' },
        { id: 'dashboard.analytics', label: 'Analytics', description: 'View detailed analytics and reports' }
      ]
    },
    bookings: {
      label: 'Bookings',
      description: 'Appointment and booking management',
      icon: '📅',
      permissions: [
        { id: 'bookings.view', label: 'View Bookings', description: 'View appointment schedules' },
        { id: 'bookings.create', label: 'Create Bookings', description: 'Create new appointments' },
        { id: 'bookings.edit', label: 'Edit Bookings', description: 'Modify existing appointments' },
        { id: 'bookings.delete', label: 'Delete Bookings', description: 'Cancel or remove appointments' },
        { id: 'bookings.export', label: 'Export Bookings', description: 'Export booking data' }
      ]
    },
    practitioners: {
      label: 'Practitioners',
      description: 'Healthcare practitioner management',
      icon: '👩‍⚕️',
      permissions: [
        { id: 'practitioners.view', label: 'View Practitioners', description: 'View practitioner profiles' },
        { id: 'practitioners.manage', label: 'Manage Practitioners', description: 'Add, edit, and manage practitioners' },
        { id: 'practitioners.assign', label: 'Assign Practitioners', description: 'Assign practitioners to users/locations' }
      ]
    },
    customers: {
      label: 'Customers/Patients',
      description: 'Patient and customer management',
      icon: '👤',
      permissions: [
        { id: 'customers.view', label: 'View Customers', description: 'View customer profiles' },
        { id: 'customers.manage', label: 'Manage Customers', description: 'Add, edit customer information' },
        { id: 'customers.contact', label: 'Contact Info', description: 'Access customer contact details' },
        { id: 'customers.history', label: 'Booking History', description: 'View customer booking history' }
      ]
    },
    locations: {
      label: 'Locations/Clinics',
      description: 'Location and clinic management',
      icon: '🏥',
      permissions: [
        { id: 'locations.view', label: 'View Locations', description: 'View clinic/location information' },
        { id: 'locations.manage', label: 'Manage Locations', description: 'Add, edit locations' },
        { id: 'locations.configure', label: 'Configure Locations', description: 'Advanced location settings' }
      ]
    },
    services: {
      label: 'Services',
      description: 'Service and treatment management',
      icon: '🩺',
      permissions: [
        { id: 'services.view', label: 'View Services', description: 'View available services' },
        { id: 'services.manage', label: 'Manage Services', description: 'Add, edit services' },
        { id: 'services.pricing', label: 'Pricing Management', description: 'Set and modify service pricing' }
      ]
    },
    widgets: {
      label: 'Widgets',
      description: 'Widget and integration management',
      icon: '🔧',
      permissions: [
        { id: 'widgets.view', label: 'View Widgets', description: 'View widget configurations' },
        { id: 'widgets.create', label: 'Create Widgets', description: 'Create new widgets' },
        { id: 'widgets.configure', label: 'Configure Widgets', description: 'Modify widget settings' },
        { id: 'widgets.deploy', label: 'Deploy Widgets', description: 'Deploy widgets to websites' }
      ]
    },
    calendar: {
      label: 'Calendar',
      description: 'Calendar and scheduling management',
      icon: '📆',
      permissions: [
        { id: 'calendar.view', label: 'View Calendar', description: 'View calendar schedules' },
        { id: 'calendar.manage_own', label: 'Manage Own Calendar', description: 'Manage personal calendar' },
        { id: 'calendar.manage_others', label: 'Manage Others Calendars', description: 'Manage other users calendars' },
        { id: 'calendar.sync_external', label: 'External Calendar Sync', description: 'Sync with Google/Outlook calendars' }
      ]
    },
    users: {
      label: 'User Management',
      description: 'System user and access management',
      icon: '👥',
      permissions: [
        { id: 'users.view', label: 'View Users', description: 'View user accounts' },
        { id: 'users.create', label: 'Create Users', description: 'Create new user accounts' },
        { id: 'users.edit', label: 'Edit Users', description: 'Modify user account details' },
        { id: 'users.roles', label: 'Assign Roles', description: 'Assign roles to users' },
        { id: 'users.practitioners', label: 'Assign Practitioners', description: 'Assign practitioners to users' }
      ]
    },
    settings: {
      label: 'Settings',
      description: 'System and practice settings',
      icon: '⚙️',
      permissions: [
        { id: 'settings.practice', label: 'Practice Settings', description: 'Modify practice-wide settings' },
        { id: 'settings.integrations', label: 'Integrations', description: 'Configure third-party integrations' },
        { id: 'settings.billing', label: 'Billing Settings', description: 'Configure billing and payments' }
      ]
    }
  };

  // Pre-defined healthcare roles
  const roleTemplates = {
    'super-admin': {
      name: 'Super Admin',
      description: 'Complete system access across all practices and features',
      color: '#dc2626',
      hierarchyLevel: 1,
      permissions: Object.values(permissionCategories).flatMap(cat => cat.permissions.map(p => p.id)),
      restrictions: {
        locationSpecific: false,
        timeRestricted: false,
        requiresApproval: false
      }
    },
    'practice-manager': {
      name: 'Practice Manager',
      description: 'Full practice management with oversight of all clinic operations',
      color: '#7c3aed',
      hierarchyLevel: 2,
      permissions: [
        'dashboard.view', 'dashboard.analytics',
        'bookings.view', 'bookings.create', 'bookings.edit', 'bookings.delete', 'bookings.export',
        'practitioners.view', 'practitioners.manage', 'practitioners.assign',
        'customers.view', 'customers.manage', 'customers.contact', 'customers.history',
        'locations.view', 'locations.manage', 'locations.configure',
        'services.view', 'services.manage', 'services.pricing',
        'widgets.view', 'widgets.create', 'widgets.configure', 'widgets.deploy',
        'calendar.view', 'calendar.manage_own', 'calendar.manage_others', 'calendar.sync_external',
        'users.view', 'users.create', 'users.edit', 'users.roles', 'users.practitioners',
        'settings.practice', 'settings.integrations'
      ],
      restrictions: {
        locationSpecific: false,
        timeRestricted: false,
        requiresApproval: false
      }
    },
    'clinic-manager': {
      name: 'Clinic Manager',
      description: 'Management access for specific clinic locations',
      color: '#2563eb',
      hierarchyLevel: 3,
      permissions: [
        'dashboard.view', 'dashboard.analytics',
        'bookings.view', 'bookings.create', 'bookings.edit', 'bookings.delete',
        'practitioners.view', 'practitioners.assign',
        'customers.view', 'customers.manage', 'customers.contact', 'customers.history',
        'locations.view', 'locations.manage',
        'services.view', 'services.manage',
        'calendar.view', 'calendar.manage_own', 'calendar.manage_others',
        'users.view', 'users.practitioners'
      ],
      restrictions: {
        locationSpecific: true,
        timeRestricted: false,
        requiresApproval: true
      }
    },
    'practitioner': {
      name: 'Practitioner',
      description: 'Healthcare provider with access to own schedule and patient data',
      color: '#059669',
      hierarchyLevel: 4,
      permissions: [
        'dashboard.view',
        'bookings.view', 'bookings.create', 'bookings.edit',
        'customers.view', 'customers.history',
        'services.view',
        'calendar.view', 'calendar.manage_own', 'calendar.sync_external'
      ],
      restrictions: {
        locationSpecific: true,
        timeRestricted: false,
        requiresApproval: false,
        ownDataOnly: true
      }
    },
    'receptionist': {
      name: 'Receptionist',
      description: 'Front desk operations with booking and customer service access',
      color: '#dc2626',
      hierarchyLevel: 5,
      permissions: [
        'dashboard.view',
        'bookings.view', 'bookings.create', 'bookings.edit',
        'practitioners.view',
        'customers.view', 'customers.manage', 'customers.contact', 'customers.history',
        'services.view',
        'calendar.view'
      ],
      restrictions: {
        locationSpecific: true,
        timeRestricted: true,
        requiresApproval: false
      }
    },
    'admin-assistant': {
      name: 'Admin Assistant',
      description: 'Administrative support with limited access to bookings and customer data',
      color: '#7c2d12',
      hierarchyLevel: 6,
      permissions: [
        'dashboard.view',
        'bookings.view', 'bookings.create',
        'practitioners.view',
        'customers.view', 'customers.contact',
        'services.view',
        'calendar.view'
      ],
      restrictions: {
        locationSpecific: true,
        timeRestricted: true,
        requiresApproval: false
      }
    },
    'read-only': {
      name: 'Read-Only User',
      description: 'View-only access for reports and monitoring',
      color: '#6b7280',
      hierarchyLevel: 7,
      permissions: [
        'dashboard.view',
        'bookings.view',
        'practitioners.view',
        'customers.view',
        'locations.view',
        'services.view',
        'calendar.view'
      ],
      restrictions: {
        locationSpecific: true,
        timeRestricted: true,
        requiresApproval: true
      }
    }
  };

  useEffect(() => {
    // Initialize roles data
    const initialRoles = Object.entries(roleTemplates).map(([id, template]) => ({
      id,
      ...template,
      isSystem: true,
      createdDate: '2024-01-01',
      lastModified: '2024-01-01',
      assignedUsers: 0
    }));

    // Add some custom roles
    initialRoles.push({
      id: 'custom-billing-admin',
      name: 'Billing Administrator',
      description: 'Specialized role for billing and payment management',
      color: '#ea580c',
      hierarchyLevel: 4,
      permissions: [
        'dashboard.view', 'dashboard.analytics',
        'customers.view', 'customers.contact', 'customers.history',
        'services.view', 'services.pricing',
        'settings.billing'
      ],
      restrictions: {
        locationSpecific: false,
        timeRestricted: false,
        requiresApproval: true
      },
      isSystem: false,
      createdDate: '2024-09-01',
      lastModified: '2024-09-10',
      assignedUsers: 2
    });

    setRoles(initialRoles);

    // Initialize users (simplified from UserManagementView)
    const initialUsers = [
      {
        id: 'user-001',
        name: 'Dr. Sarah Mitchell',
        email: 'sarah.mitchell@medidev.com',
        currentRole: 'practice-manager',
        clinics: ['harley-street'],
        status: 'active'
      },
      {
        id: 'user-002',
        name: 'Emily Johnson',
        email: 'emily.johnson@medidev.com',
        currentRole: 'receptionist',
        clinics: ['harley-street'],
        status: 'active'
      },
      {
        id: 'user-003',
        name: 'Dr. Michael Chen',
        email: 'michael.chen@medidev.com',
        currentRole: 'practitioner',
        clinics: ['harley-street'],
        status: 'active'
      }
    ];

    setUsers(initialUsers);

    // Initialize role assignments
    const initialAssignments = [
      {
        id: 'assign-001',
        userId: 'user-001',
        roleId: 'practice-manager',
        assignedBy: 'system',
        assignedDate: '2024-01-15',
        expiryDate: null,
        restrictions: {
          locations: ['harley-street', 'canary-wharf'],
          timeSlots: null
        }
      },
      {
        id: 'assign-002',
        userId: 'user-002',
        roleId: 'receptionist',
        assignedBy: 'user-001',
        assignedDate: '2024-02-20',
        expiryDate: null,
        restrictions: {
          locations: ['harley-street'],
          timeSlots: { start: '08:00', end: '18:00', days: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'] }
        }
      }
    ];

    setRoleAssignments(initialAssignments);

    // Initialize audit log
    const initialAuditLog = [
      {
        id: 'audit-001',
        action: 'role_created',
        details: 'Custom role "Billing Administrator" created',
        performedBy: 'user-001',
        performedAt: '2024-09-01T10:30:00Z',
        affectedUser: null,
        roleId: 'custom-billing-admin'
      },
      {
        id: 'audit-002',
        action: 'role_assigned',
        details: 'Role "Receptionist" assigned to Emily Johnson',
        performedBy: 'user-001',
        performedAt: '2024-02-20T14:15:00Z',
        affectedUser: 'user-002',
        roleId: 'receptionist'
      }
    ];

    setAuditLog(initialAuditLog);
  }, []);

  const availableClinics = [
    { id: 'harley-street', name: 'Harley Street Practice' },
    { id: 'canary-wharf', name: 'Canary Wharf Clinic' },
    { id: 'kensington', name: 'Kensington Wellness Hub' },
    { id: 'greenwich', name: 'Greenwich Community Clinic' }
  ];

  const filteredRoles = roles.filter(role => {
    const matchesStatus = filterStatus === 'all' || 
      (filterStatus === 'system' && role.isSystem) ||
      (filterStatus === 'custom' && !role.isSystem);
    const matchesSearch = role.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         role.description.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const getRoleUserCount = (roleId) => {
    return roleAssignments.filter(a => a.roleId === roleId).length;
  };

  // Slideout handlers
  const handleCreateRole = () => {
    const formContent = (
      <RoleForm
        roleTemplates={roleTemplates}
        permissionCategories={permissionCategories}
        availableClinics={availableClinics}
        onSubmit={handleSubmitNewRole}
        onCancel={closeSlideout}
      />
    );
    openSlideout('Create New Role', formContent, '900px');
  };

  const handleEditRole = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    const formContent = (
      <RoleForm
        role={role}
        roleTemplates={roleTemplates}
        permissionCategories={permissionCategories}
        availableClinics={availableClinics}
        onSubmit={(data) => handleSubmitEditRole(roleId, data)}
        onCancel={closeSlideout}
        isEdit={true}
      />
    );
    openSlideout('Edit Role', formContent, '900px');
  };

  const handleViewRole = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    const assignedUsers = roleAssignments
      .filter(a => a.roleId === roleId)
      .map(a => users.find(u => u.id === a.userId))
      .filter(u => u);

    const detailContent = (
      <RoleDetail
        role={role}
        permissionCategories={permissionCategories}
        assignedUsers={assignedUsers}
        onEdit={() => handleEditRole(roleId)}
        onAssignUsers={() => handleBulkAssignRole(roleId)}
      />
    );
    openSlideout('Role Details', detailContent, '800px');
  };

  const handleBulkAssignRole = (roleId) => {
    const role = roles.find(r => r.id === roleId);
    if (!role) return;

    const currentAssignments = roleAssignments.filter(a => a.roleId === roleId);

    const formContent = (
      <BulkRoleAssignmentForm
        role={role}
        users={users}
        currentAssignments={currentAssignments}
        availableClinics={availableClinics}
        onSubmit={(assignments) => handleSubmitBulkAssignment(roleId, assignments)}
        onCancel={closeSlideout}
      />
    );
    openSlideout('Bulk Role Assignment', formContent, '800px');
  };

  const handleViewPermissionMatrix = () => {
    const matrixContent = (
      <PermissionMatrix
        roles={roles}
        permissionCategories={permissionCategories}
        onEditRole={handleEditRole}
      />
    );
    openSlideout('Permissions Matrix', matrixContent, '1200px');
  };

  const handleViewAuditLog = () => {
    const auditContent = (
      <AuditLog
        auditLog={auditLog}
        users={users}
        roles={roles}
      />
    );
    openSlideout('Role Changes Audit Log', auditContent, '1000px');
  };

  // CRUD operations
  const handleSubmitNewRole = async (formData) => {
    try {
      const newRole = {
        id: `custom-${Date.now()}`,
        name: formData.name,
        description: formData.description,
        color: formData.color,
        hierarchyLevel: formData.hierarchyLevel,
        permissions: formData.permissions,
        restrictions: formData.restrictions,
        isSystem: false,
        createdDate: new Date().toISOString().split('T')[0],
        lastModified: new Date().toISOString().split('T')[0],
        assignedUsers: 0
      };

      const updatedRoles = [...roles, newRole];
      setRoles(updatedRoles);

      // Add audit log entry
      const auditEntry = {
        id: `audit-${Date.now()}`,
        action: 'role_created',
        details: `Custom role "${newRole.name}" created`,
        performedBy: 'current-user',
        performedAt: new Date().toISOString(),
        affectedUser: null,
        roleId: newRole.id
      };
      setAuditLog(prev => [auditEntry, ...prev]);

      closeSlideout();
      showNotification(`Role "${newRole.name}" created successfully!`, 'success');
    } catch (error) {
      showNotification('Error creating role. Please try again.', 'error');
    }
  };

  const handleSubmitEditRole = async (roleId, formData) => {
    try {
      const updatedRoles = roles.map(role => 
        role.id === roleId 
          ? {
              ...role,
              name: formData.name,
              description: formData.description,
              color: formData.color,
              hierarchyLevel: formData.hierarchyLevel,
              permissions: formData.permissions,
              restrictions: formData.restrictions,
              lastModified: new Date().toISOString().split('T')[0]
            }
          : role
      );

      setRoles(updatedRoles);

      // Add audit log entry
      const auditEntry = {
        id: `audit-${Date.now()}`,
        action: 'role_modified',
        details: `Role "${formData.name}" was modified`,
        performedBy: 'current-user',
        performedAt: new Date().toISOString(),
        affectedUser: null,
        roleId: roleId
      };
      setAuditLog(prev => [auditEntry, ...prev]);

      closeSlideout();
      showNotification(`Role "${formData.name}" updated successfully!`, 'success');
    } catch (error) {
      showNotification('Error updating role. Please try again.', 'error');
    }
  };

  const handleSubmitBulkAssignment = (roleId, assignments) => {
    try {
      // Remove existing assignments for this role
      const filteredAssignments = roleAssignments.filter(a => a.roleId !== roleId);
      
      // Add new assignments
      const newAssignments = assignments.map(assignment => ({
        id: `assign-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        userId: assignment.userId,
        roleId: roleId,
        assignedBy: 'current-user',
        assignedDate: new Date().toISOString().split('T')[0],
        expiryDate: assignment.expiryDate || null,
        restrictions: assignment.restrictions || {}
      }));

      const updatedAssignments = [...filteredAssignments, ...newAssignments];
      setRoleAssignments(updatedAssignments);

      // Update role user counts
      const updatedRoles = roles.map(role => 
        role.id === roleId 
          ? { ...role, assignedUsers: newAssignments.length }
          : role
      );
      setRoles(updatedRoles);

      // Add audit log entries
      const auditEntries = newAssignments.map(assignment => ({
        id: `audit-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        action: 'role_assigned',
        details: `Role assigned to user`,
        performedBy: 'current-user',
        performedAt: new Date().toISOString(),
        affectedUser: assignment.userId,
        roleId: roleId
      }));
      setAuditLog(prev => [...auditEntries, ...prev]);

      closeSlideout();
      showNotification(`Role assignments updated successfully!`, 'success');
    } catch (error) {
      showNotification('Error updating role assignments. Please try again.', 'error');
    }
  };

  return (
    <div className="roles-permissions-view">
      {/* Header */}
      <PageHeader pageKey="roles">
        <button
          className="btn btn-secondary"
          onClick={handleViewPermissionMatrix}
        >
          📊 Permission Matrix
        </button>
        <button
          className="btn btn-secondary"
          onClick={handleViewAuditLog}
        >
          📋 Audit Log
        </button>
        <button
          className="btn btn-primary"
          onClick={handleCreateRole}
        >
          <span className="icon icon--add icon--success"></span>
          Create Role
        </button>
      </PageHeader>

      {/* View Tabs */}
      <div className="view-tabs">
        <button 
          className={`tab ${selectedView === 'roles' ? 'active' : ''}`}
          onClick={() => setSelectedView('roles')}
        >
          Roles ({roles.length})
        </button>
        <button 
          className={`tab ${selectedView === 'assignments' ? 'active' : ''}`}
          onClick={() => setSelectedView('assignments')}
        >
          Assignments ({roleAssignments.length})
        </button>
        <button 
          className={`tab ${selectedView === 'templates' ? 'active' : ''}`}
          onClick={() => setSelectedView('templates')}
        >
          Templates ({Object.keys(roleTemplates).length})
        </button>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <div className="filter-group">
          <input
            type="text"
            className="search-input"
            placeholder="Search roles..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="filter-group">
          <select 
            className="filter-select" 
            value={filterStatus} 
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <option value="all">All Roles</option>
            <option value="system">System Roles</option>
            <option value="custom">Custom Roles</option>
          </select>
        </div>
      </div>

      {/* Content based on selected view */}
      <div className="view-content">
        {selectedView === 'roles' && (
          <RolesGrid 
            roles={filteredRoles}
            permissionCategories={permissionCategories}
            getRoleUserCount={getRoleUserCount}
            onViewRole={handleViewRole}
            onEditRole={handleEditRole}
            onAssignUsers={handleBulkAssignRole}
          />
        )}

        {selectedView === 'assignments' && (
          <AssignmentsTable 
            roleAssignments={roleAssignments}
            users={users}
            roles={roles}
            availableClinics={availableClinics}
          />
        )}

        {selectedView === 'templates' && (
          <RoleTemplates 
            roleTemplates={roleTemplates}
            permissionCategories={permissionCategories}
            onCreateFromTemplate={(templateId) => {
              const template = roleTemplates[templateId];
              const formContent = (
                <RoleForm
                  roleTemplates={roleTemplates}
                  permissionCategories={permissionCategories}
                  availableClinics={availableClinics}
                  template={template}
                  onSubmit={handleSubmitNewRole}
                  onCancel={closeSlideout}
                />
              );
              openSlideout('Create Role from Template', formContent, '900px');
            }}
          />
        )}
      </div>

      <SlideoutPanel
        isOpen={isOpen}
        onClose={closeSlideout}
        title={title}
        width={width}
      >
        {content}
      </SlideoutPanel>
    </div>
  );
};

// Roles Grid Component
const RolesGrid = ({ roles, permissionCategories, getRoleUserCount, onViewRole, onEditRole, onAssignUsers }) => {
  const getPermissionCount = (permissions) => permissions.length;
  
  const getCategoryBreakdown = (permissions) => {
    const breakdown = {};
    Object.entries(permissionCategories).forEach(([catId, category]) => {
      const count = permissions.filter(p => p.startsWith(catId + '.')).length;
      if (count > 0) {
        breakdown[category.label] = count;
      }
    });
    return breakdown;
  };

  return (
    <div className="roles-grid">
      {roles.map(role => {
        const userCount = getRoleUserCount(role.id);
        const permissionBreakdown = getCategoryBreakdown(role.permissions);
        
        return (
          <div key={role.id} className="role-card">
            <div className="role-header">
              <div className="role-info">
                <div 
                  className="role-color-indicator" 
                  style={{ backgroundColor: role.color }}
                ></div>
                <div>
                  <h4>{role.name}</h4>
                  <p className="role-description">{role.description}</p>
                </div>
              </div>
              <div className="role-badges">
                {role.isSystem && <span className="badge badge-system">System</span>}
                <span className="badge badge-level">Level {role.hierarchyLevel}</span>
              </div>
            </div>

            <div className="role-stats">
              <div className="stat">
                <span className="stat-value">{getPermissionCount(role.permissions)}</span>
                <span className="stat-label">Permissions</span>
              </div>
              <div className="stat">
                <span className="stat-value">{userCount}</span>
                <span className="stat-label">Users</span>
              </div>
            </div>

            <div className="permission-breakdown">
              <h5>Permission Categories:</h5>
              <div className="breakdown-list">
                {Object.entries(permissionBreakdown).map(([category, count]) => (
                  <div key={category} className="breakdown-item">
                    <span>{category}</span>
                    <span className="count">{count}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="role-actions">
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => onViewRole(role.id)}
              >
                View Details
              </button>
              <button 
                className="btn btn-sm btn-secondary"
                onClick={() => onEditRole(role.id)}
                disabled={role.isSystem}
              >
                Edit
              </button>
              <button 
                className="btn btn-sm btn-primary"
                onClick={() => onAssignUsers(role.id)}
              >
                Assign Users
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Role Form Component
const RoleForm = ({ role = null, roleTemplates, permissionCategories, availableClinics, template = null, onSubmit, onCancel, isEdit = false }) => {
  const [formData, setFormData] = useState({
    name: role?.name || template?.name || '',
    description: role?.description || template?.description || '',
    color: role?.color || template?.color || '#6b7280',
    hierarchyLevel: role?.hierarchyLevel || template?.hierarchyLevel || 7,
    permissions: role?.permissions || template?.permissions || [],
    restrictions: role?.restrictions || template?.restrictions || {
      locationSpecific: false,
      timeRestricted: false,
      requiresApproval: false
    }
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name || !formData.description) {
      alert('Please fill in all required fields');
      return;
    }
    if (formData.permissions.length === 0) {
      alert('Please select at least one permission');
      return;
    }
    onSubmit(formData);
  };

  const handlePermissionToggle = (permissionId) => {
    const hasPermission = formData.permissions.includes(permissionId);
    const updatedPermissions = hasPermission
      ? formData.permissions.filter(p => p !== permissionId)
      : [...formData.permissions, permissionId];
    setFormData({ ...formData, permissions: updatedPermissions });
  };

  const handleCategoryToggle = (categoryId, allPermissions) => {
    const categoryPermissions = allPermissions.map(p => p.id);
    const hasAllCategoryPerms = categoryPermissions.every(p => formData.permissions.includes(p));
    
    let updatedPermissions;
    if (hasAllCategoryPerms) {
      // Remove all category permissions
      updatedPermissions = formData.permissions.filter(p => !categoryPermissions.includes(p));
    } else {
      // Add all missing category permissions
      const missingPerms = categoryPermissions.filter(p => !formData.permissions.includes(p));
      updatedPermissions = [...formData.permissions, ...missingPerms];
    }
    
    setFormData({ ...formData, permissions: updatedPermissions });
  };

  const getCategoryPermissionCount = (categoryPermissions) => {
    return categoryPermissions.filter(p => formData.permissions.includes(p.id)).length;
  };

  return (
    <form onSubmit={handleSubmit} className="role-form">
      <div className="form-sections">
        {/* Basic Information */}
        <div className="form-section">
          <h4>Basic Information</h4>
          <div className="form-grid">
            <div className="form-group">
              <label htmlFor="name">Role Name *</label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                placeholder="Enter role name"
                required
              />
            </div>
            <div className="form-group">
              <label htmlFor="color">Role Color</label>
              <input
                id="color"
                type="color"
                value={formData.color}
                onChange={(e) => setFormData({ ...formData, color: e.target.value })}
              />
            </div>
            <div className="form-group">
              <label htmlFor="hierarchyLevel">Hierarchy Level</label>
              <select
                id="hierarchyLevel"
                value={formData.hierarchyLevel}
                onChange={(e) => setFormData({ ...formData, hierarchyLevel: parseInt(e.target.value) })}
              >
                <option value={1}>Level 1 - Super Admin</option>
                <option value={2}>Level 2 - Practice Manager</option>
                <option value={3}>Level 3 - Clinic Manager</option>
                <option value={4}>Level 4 - Practitioner</option>
                <option value={5}>Level 5 - Receptionist</option>
                <option value={6}>Level 6 - Admin Assistant</option>
                <option value={7}>Level 7 - Read Only</option>
              </select>
            </div>
          </div>
          
          <div className="form-group">
            <label htmlFor="description">Description *</label>
            <textarea
              id="description"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Describe the purpose and scope of this role..."
              rows="3"
              required
            />
          </div>
        </div>

        {/* Restrictions */}
        <div className="form-section">
          <h4>Access Restrictions</h4>
          <div className="restrictions-grid">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.restrictions.locationSpecific}
                onChange={(e) => setFormData({
                  ...formData,
                  restrictions: { ...formData.restrictions, locationSpecific: e.target.checked }
                })}
              />
              <span className="checkmark"></span>
              Location-Specific Access
            </label>
            
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.restrictions.timeRestricted}
                onChange={(e) => setFormData({
                  ...formData,
                  restrictions: { ...formData.restrictions, timeRestricted: e.target.checked }
                })}
              />
              <span className="checkmark"></span>
              Time-Restricted Access
            </label>
            
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={formData.restrictions.requiresApproval}
                onChange={(e) => setFormData({
                  ...formData,
                  restrictions: { ...formData.restrictions, requiresApproval: e.target.checked }
                })}
              />
              <span className="checkmark"></span>
              Requires Approval for Assignment
            </label>
          </div>
        </div>

        {/* Permissions */}
        <div className="form-section">
          <h4>Permissions ({formData.permissions.length} selected)</h4>
          <div className="permissions-categories">
            {Object.entries(permissionCategories).map(([categoryId, category]) => {
              const categoryPermissionCount = getCategoryPermissionCount(category.permissions);
              const hasAllCategoryPerms = categoryPermissionCount === category.permissions.length;
              
              return (
                <div key={categoryId} className="permission-category">
                  <div className="category-header">
                    <div className="category-info">
                      <span className="category-icon">{category.icon}</span>
                      <div>
                        <h5>{category.label}</h5>
                        <p>{category.description}</p>
                      </div>
                    </div>
                    <div className="category-controls">
                      <span className="permission-count">
                        {categoryPermissionCount} of {category.permissions.length}
                      </span>
                      <button
                        type="button"
                        className={`btn btn-sm ${hasAllCategoryPerms ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => handleCategoryToggle(categoryId, category.permissions)}
                      >
                        {hasAllCategoryPerms ? 'Deselect All' : 'Select All'}
                      </button>
                    </div>
                  </div>
                  
                  <div className="category-permissions">
                    {category.permissions.map(permission => (
                      <label key={permission.id} className="permission-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.permissions.includes(permission.id)}
                          onChange={() => handlePermissionToggle(permission.id)}
                        />
                        <span className="checkmark"></span>
                        <div className="permission-info">
                          <span className="permission-label">{permission.label}</span>
                          <span className="permission-description">{permission.description}</span>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary">
          {isEdit ? 'Update Role' : 'Create Role'}
        </button>
      </div>
    </form>
  );
};

// Role Detail Component
const RoleDetail = ({ role, permissionCategories, assignedUsers, onEdit, onAssignUsers }) => {
  const getCategoryPermissions = (categoryId) => {
    const category = permissionCategories[categoryId];
    if (!category) return [];
    
    return category.permissions.filter(p => role.permissions.includes(p.id));
  };

  return (
    <div className="role-detail">
      <div className="detail-sections">
        {/* Role Header */}
        <div className="detail-header">
          <div 
            className="role-color-indicator-lg" 
            style={{ backgroundColor: role.color }}
          ></div>
          <div className="role-header-info">
            <h3>{role.name}</h3>
            <p className="role-description">{role.description}</p>
            <div className="role-badges">
              {role.isSystem && <span className="badge badge-system">System Role</span>}
              <span className="badge badge-level">Hierarchy Level {role.hierarchyLevel}</span>
            </div>
          </div>
        </div>

        {/* Role Statistics */}
        <div className="detail-section">
          <h4>Role Statistics</h4>
          <div className="role-stats-detailed">
            <div className="stat-item">
              <span className="stat-value">{role.permissions.length}</span>
              <span className="stat-label">Total Permissions</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{assignedUsers.length}</span>
              <span className="stat-label">Assigned Users</span>
            </div>
            <div className="stat-item">
              <span className="stat-value">{role.hierarchyLevel}</span>
              <span className="stat-label">Hierarchy Level</span>
            </div>
          </div>
        </div>

        {/* Assigned Users */}
        {assignedUsers.length > 0 && (
          <div className="detail-section">
            <h4>Assigned Users ({assignedUsers.length})</h4>
            <div className="assigned-users-list">
              {assignedUsers.map(user => (
                <div key={user.id} className="assigned-user-item">
                  <div className="user-avatar-sm">{user.name.split(' ').map(n => n[0]).join('')}</div>
                  <div className="user-info">
                    <span className="user-name">{user.name}</span>
                    <span className="user-email">{user.email}</span>
                  </div>
                  <span className={`user-status status-${user.status}`}>{user.status}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Permissions by Category */}
        <div className="detail-section">
          <h4>Permissions by Category</h4>
          <div className="permissions-by-category">
            {Object.entries(permissionCategories).map(([categoryId, category]) => {
              const categoryPermissions = getCategoryPermissions(categoryId);
              if (categoryPermissions.length === 0) return null;
              
              return (
                <div key={categoryId} className="category-permissions-detail">
                  <div className="category-header-detail">
                    <span className="category-icon">{category.icon}</span>
                    <div>
                      <h5>{category.label}</h5>
                      <span className="permission-count">
                        {categoryPermissions.length} of {category.permissions.length} permissions
                      </span>
                    </div>
                  </div>
                  <div className="category-permissions-list">
                    {categoryPermissions.map(permission => (
                      <div key={permission.id} className="permission-item-detail">
                        <span className="permission-name">{permission.label}</span>
                        <span className="permission-description">{permission.description}</span>
                      </div>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Access Restrictions */}
        <div className="detail-section">
          <h4>Access Restrictions</h4>
          <div className="restrictions-list">
            <div className="restriction-item">
              <span className="restriction-label">Location-Specific Access:</span>
              <span className={`restriction-value ${role.restrictions.locationSpecific ? 'enabled' : 'disabled'}`}>
                {role.restrictions.locationSpecific ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="restriction-item">
              <span className="restriction-label">Time-Restricted Access:</span>
              <span className={`restriction-value ${role.restrictions.timeRestricted ? 'enabled' : 'disabled'}`}>
                {role.restrictions.timeRestricted ? 'Yes' : 'No'}
              </span>
            </div>
            <div className="restriction-item">
              <span className="restriction-label">Requires Approval:</span>
              <span className={`restriction-value ${role.restrictions.requiresApproval ? 'enabled' : 'disabled'}`}>
                {role.restrictions.requiresApproval ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </div>

        {/* Role Metadata */}
        <div className="detail-section">
          <h4>Role Information</h4>
          <div className="role-metadata">
            <div className="metadata-item">
              <span className="metadata-label">Created Date:</span>
              <span>{new Date(role.createdDate).toLocaleDateString()}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Last Modified:</span>
              <span>{new Date(role.lastModified).toLocaleDateString()}</span>
            </div>
            <div className="metadata-item">
              <span className="metadata-label">Role Type:</span>
              <span>{role.isSystem ? 'System Role' : 'Custom Role'}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="detail-actions">
        <button onClick={onAssignUsers} className="btn btn-secondary">
          Manage User Assignments
        </button>
        <button 
          onClick={onEdit} 
          className="btn btn-primary"
          disabled={role.isSystem}
        >
          Edit Role
        </button>
      </div>
    </div>
  );
};

// Bulk Role Assignment Form Component
const BulkRoleAssignmentForm = ({ role, users, currentAssignments, availableClinics, onSubmit, onCancel }) => {
  const [assignments, setAssignments] = useState(
    currentAssignments.map(assignment => {
      const user = users.find(u => u.id === assignment.userId);
      return {
        userId: assignment.userId,
        userName: user?.name || 'Unknown User',
        userEmail: user?.email || '',
        expiryDate: assignment.expiryDate || '',
        restrictions: assignment.restrictions || {}
      };
    })
  );

  const handleAddUser = () => {
    const assignedUserIds = assignments.map(a => a.userId);
    const availableUsers = users.filter(u => !assignedUserIds.includes(u.id) && u.status === 'active');
    
    if (availableUsers.length > 0) {
      const newUser = availableUsers[0];
      setAssignments([
        ...assignments,
        {
          userId: newUser.id,
          userName: newUser.name,
          userEmail: newUser.email,
          expiryDate: '',
          restrictions: {
            locations: role.restrictions.locationSpecific ? [] : null,
            timeSlots: role.restrictions.timeRestricted ? null : null
          }
        }
      ]);
    }
  };

  const handleRemoveUser = (index) => {
    setAssignments(assignments.filter((_, i) => i !== index));
  };

  const handleUserChange = (index, userId) => {
    const user = users.find(u => u.id === userId);
    if (!user) return;
    
    const updatedAssignments = assignments.map((assignment, i) => 
      i === index 
        ? { 
            ...assignment, 
            userId: user.id, 
            userName: user.name, 
            userEmail: user.email 
          }
        : assignment
    );
    setAssignments(updatedAssignments);
  };

  const handleExpiryDateChange = (index, date) => {
    const updatedAssignments = assignments.map((assignment, i) => 
      i === index ? { ...assignment, expiryDate: date } : assignment
    );
    setAssignments(updatedAssignments);
  };

  const handleLocationRestrictionChange = (index, locations) => {
    const updatedAssignments = assignments.map((assignment, i) => 
      i === index 
        ? { 
            ...assignment, 
            restrictions: { 
              ...assignment.restrictions, 
              locations 
            } 
          }
        : assignment
    );
    setAssignments(updatedAssignments);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate assignments
    for (let i = 0; i < assignments.length; i++) {
      const assignment = assignments[i];
      if (!assignment.userId) {
        alert(`Please select a user for assignment ${i + 1}`);
        return;
      }
      
      if (role.restrictions.locationSpecific && (!assignment.restrictions.locations || assignment.restrictions.locations.length === 0)) {
        alert(`Please select at least one location for ${assignment.userName}`);
        return;
      }
    }
    
    onSubmit(assignments);
  };

  const assignedUserIds = assignments.map(a => a.userId);
  const availableUsers = users.filter(u => !assignedUserIds.includes(u.id) && u.status === 'active');

  return (
    <form onSubmit={handleSubmit} className="bulk-assignment-form">
      <div className="form-header">
        <div className="role-context">
          <div 
            className="role-color-indicator-sm" 
            style={{ backgroundColor: role.color }}
          ></div>
          <div>
            <h4>{role.name}</h4>
            <p>{role.description}</p>
          </div>
        </div>
      </div>

      <div className="assignments-section">
        <div className="section-header">
          <h4>User Assignments ({assignments.length})</h4>
          <button
            type="button"
            onClick={handleAddUser}
            className="btn btn-secondary btn-sm"
            disabled={availableUsers.length === 0}
          >
            + Add User
          </button>
        </div>

        {assignments.length === 0 ? (
          <div className="no-assignments">
            <p>No users assigned. Click "Add User" to get started.</p>
          </div>
        ) : (
          <div className="assignments-list">
            {assignments.map((assignment, index) => (
              <div key={index} className="assignment-item">
                <div className="assignment-header">
                  <h5>Assignment #{index + 1}</h5>
                  <button
                    type="button"
                    onClick={() => handleRemoveUser(index)}
                    className="btn btn-danger btn-sm"
                  >
                    Remove
                  </button>
                </div>

                <div className="assignment-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label>User</label>
                      <select
                        value={assignment.userId}
                        onChange={(e) => handleUserChange(index, e.target.value)}
                        required
                      >
                        <option value="">Select User</option>
                        {users
                          .filter(u => u.status === 'active')
                          .filter(u => u.id === assignment.userId || !assignedUserIds.includes(u.id))
                          .map(user => (
                            <option key={user.id} value={user.id}>
                              {user.name} - {user.email}
                            </option>
                          ))
                        }
                      </select>
                    </div>

                    <div className="form-group">
                      <label>Expiry Date (Optional)</label>
                      <input
                        type="date"
                        value={assignment.expiryDate}
                        onChange={(e) => handleExpiryDateChange(index, e.target.value)}
                        min={new Date().toISOString().split('T')[0]}
                      />
                    </div>
                  </div>

                  {role.restrictions.locationSpecific && (
                    <div className="form-group">
                      <label>Location Access</label>
                      <div className="location-checkboxes">
                        {availableClinics.map(clinic => (
                          <label key={clinic.id} className="checkbox-label">
                            <input
                              type="checkbox"
                              checked={assignment.restrictions.locations?.includes(clinic.id) || false}
                              onChange={(e) => {
                                const currentLocations = assignment.restrictions.locations || [];
                                const updatedLocations = e.target.checked
                                  ? [...currentLocations, clinic.id]
                                  : currentLocations.filter(id => id !== clinic.id);
                                handleLocationRestrictionChange(index, updatedLocations);
                              }}
                            />
                            <span className="checkmark"></span>
                            {clinic.name}
                          </label>
                        ))}
                      </div>
                    </div>
                  )}

                  {assignment.userId && (
                    <div className="assignment-summary">
                      <strong>Summary:</strong> {assignment.userName} ({assignment.userEmail})
                      {assignment.expiryDate && ` - Expires: ${new Date(assignment.expiryDate).toLocaleDateString()}`}
                      {role.restrictions.locationSpecific && assignment.restrictions.locations && (
                        <span> - Locations: {assignment.restrictions.locations.length}</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
        <button type="submit" className="btn btn-primary" disabled={assignments.length === 0}>
          Save Assignments ({assignments.length})
        </button>
      </div>
    </form>
  );
};

// Assignments Table Component
const AssignmentsTable = ({ roleAssignments, users, roles, availableClinics }) => {
  const getUser = (userId) => users.find(u => u.id === userId);
  const getRole = (roleId) => roles.find(r => r.id === roleId);
  const getClinicNames = (locationIds) => {
    if (!locationIds || locationIds.length === 0) return 'All Locations';
    return locationIds.map(id => {
      const clinic = availableClinics.find(c => c.id === id);
      return clinic ? clinic.name : 'Unknown';
    }).join(', ');
  };

  return (
    <div className="assignments-table-container">
      <div className="material-table-container">
        <div className="material-table-header">
          <h3 className="material-table-title">Role Assignments</h3>
          <span className="material-table-meta">Showing {roleAssignments.length} assignments</span>
        </div>

        <table className="material-table">
          <thead>
            <tr>
              <th className="material-th">User</th>
              <th className="material-th">Role</th>
              <th className="material-th">Assigned By</th>
              <th className="material-th">Assigned Date</th>
              <th className="material-th">Expiry Date</th>
              <th className="material-th">Location Access</th>
              <th className="material-th">Status</th>
            </tr>
          </thead>
          <tbody>
            {roleAssignments.length === 0 ? (
              <tr>
                <td colSpan="7" className="no-assignments">
                  No role assignments found
                </td>
              </tr>
            ) : (
              roleAssignments.map(assignment => {
                const user = getUser(assignment.userId);
                const role = getRole(assignment.roleId);
                const isExpired = assignment.expiryDate && new Date(assignment.expiryDate) < new Date();
                
                return (
                  <tr key={assignment.id} className="material-tr">
                    <td className="material-cell">
                      <div className="material-user-cell">
                        <div className="material-user-avatar">{user?.name?.split(' ').map(n => n[0]).join('') || '??'}</div>
                        <div className="material-user-info">
                          <span className="material-body-large">{user?.name || 'Unknown User'}</span>
                          <span className="material-body-small material-text-secondary">{user?.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="material-cell">
                      <div className="material-role-cell">
                        <div
                          className="role-color-indicator-xs"
                          style={{ backgroundColor: role?.color || '#6b7280' }}
                        ></div>
                        <span className="material-body-medium">{role?.name || 'Unknown Role'}</span>
                      </div>
                    </td>
                    <td className="material-cell">
                      <span className="material-body-medium">{assignment.assignedBy === 'system' ? 'System' : 'Administrator'}</span>
                    </td>
                    <td className="material-cell">
                      <span className="material-body-medium">{new Date(assignment.assignedDate).toLocaleDateString()}</span>
                    </td>
                    <td className="material-cell">
                      {assignment.expiryDate ? (
                        <span className={`material-body-medium ${isExpired ? 'expired-date' : ''}`}>
                          {new Date(assignment.expiryDate).toLocaleDateString()}
                        </span>
                      ) : (
                        <span className="material-body-medium">Never</span>
                      )}
                    </td>
                    <td className="material-cell">
                      <span className="material-body-medium">{getClinicNames(assignment.restrictions?.locations)}</span>
                    </td>
                    <td className="material-cell">
                      <span className={`material-chip--${isExpired ? 'error' : 'success'}`}>
                        {isExpired ? 'Expired' : 'Active'}
                      </span>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Role Templates Component
const RoleTemplates = ({ roleTemplates, permissionCategories, onCreateFromTemplate }) => {
  const getPermissionCount = (permissions) => permissions.length;
  
  const getCategoryBreakdown = (permissions) => {
    const breakdown = {};
    Object.entries(permissionCategories).forEach(([catId, category]) => {
      const count = permissions.filter(p => p.startsWith(catId + '.')).length;
      if (count > 0) {
        breakdown[category.label] = count;
      }
    });
    return breakdown;
  };

  return (
    <div className="role-templates">
      <div className="templates-grid">
        {Object.entries(roleTemplates).map(([templateId, template]) => {
          const permissionBreakdown = getCategoryBreakdown(template.permissions);
          
          return (
            <div key={templateId} className="template-card">
              <div className="template-header">
                <div className="template-info">
                  <div 
                    className="role-color-indicator" 
                    style={{ backgroundColor: template.color }}
                  ></div>
                  <div>
                    <h4>{template.name}</h4>
                    <p className="template-description">{template.description}</p>
                  </div>
                </div>
                <div className="template-badges">
                  <span className="badge badge-template">Template</span>
                  <span className="badge badge-level">Level {template.hierarchyLevel}</span>
                </div>
              </div>

              <div className="template-stats">
                <div className="stat">
                  <span className="stat-value">{getPermissionCount(template.permissions)}</span>
                  <span className="stat-label">Permissions</span>
                </div>
                <div className="stat">
                  <span className="stat-value">{Object.keys(permissionBreakdown).length}</span>
                  <span className="stat-label">Categories</span>
                </div>
              </div>

              <div className="template-breakdown">
                <h5>Included Categories:</h5>
                <div className="breakdown-list">
                  {Object.entries(permissionBreakdown).map(([category, count]) => (
                    <div key={category} className="breakdown-item">
                      <span>{category}</span>
                      <span className="count">{count}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="template-restrictions">
                <h5>Default Restrictions:</h5>
                <div className="restrictions-preview">
                  {template.restrictions.locationSpecific && (
                    <span className="restriction-tag">Location-Specific</span>
                  )}
                  {template.restrictions.timeRestricted && (
                    <span className="restriction-tag">Time-Restricted</span>
                  )}
                  {template.restrictions.requiresApproval && (
                    <span className="restriction-tag">Requires Approval</span>
                  )}
                  {!template.restrictions.locationSpecific && 
                   !template.restrictions.timeRestricted && 
                   !template.restrictions.requiresApproval && (
                    <span className="restriction-tag no-restrictions">No Restrictions</span>
                  )}
                </div>
              </div>

              <div className="template-actions">
                <button 
                  className="btn btn-primary"
                  onClick={() => onCreateFromTemplate(templateId)}
                >
                  Create from Template
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// Permission Matrix Component
const PermissionMatrix = ({ roles, permissionCategories, onEditRole }) => {
  const [selectedCategory, setSelectedCategory] = useState('all');

  const getFilteredPermissions = () => {
    if (selectedCategory === 'all') {
      return Object.values(permissionCategories).flatMap(cat => 
        cat.permissions.map(p => ({ ...p, category: cat.label, categoryIcon: cat.icon }))
      );
    }
    
    const category = permissionCategories[selectedCategory];
    return category ? category.permissions.map(p => ({ ...p, category: category.label, categoryIcon: category.icon })) : [];
  };

  const hasPermission = (role, permissionId) => {
    return role.permissions.includes(permissionId);
  };

  const filteredPermissions = getFilteredPermissions();

  return (
    <div className="permission-matrix">
      <div className="matrix-header">
        <div className="matrix-filters">
          <label>Filter by Category:</label>
          <select 
            value={selectedCategory}
            onChange={(e) => setSelectedCategory(e.target.value)}
            className="category-filter"
          >
            <option value="all">All Categories</option>
            {Object.entries(permissionCategories).map(([id, category]) => (
              <option key={id} value={id}>
                {category.icon} {category.label}
              </option>
            ))}
          </select>
        </div>
        <div className="matrix-legend">
          <div className="legend-item">
            <span className="permission-granted"></span>
            <span>Granted</span>
          </div>
          <div className="legend-item">
            <span className="permission-denied"></span>
            <span>Denied</span>
          </div>
        </div>
      </div>
      <div className="matrix-container">
        <div className="material-table-container">
          <div className="material-table-header">
            <h3 className="material-table-title">Permissions Matrix</h3>
            <span className="material-table-meta">Role permissions overview</span>
          </div>

          <table className="material-table matrix-table">
            <thead>
              <tr>
                <th className="material-th permission-column">Permission</th>
                {roles.map(role => (
                  <th key={role.id} className="material-th role-column">
                    <div className="role-header">
                      <div
                        className="role-color-indicator-xs"
                        style={{ backgroundColor: role.color }}
                      ></div>
                      <div className="role-name">{role.name}</div>
                      <button
                        className="btn btn-xs btn-secondary"
                        onClick={() => onEditRole(role.id)}
                        disabled={role.isSystem}
                        title={role.isSystem ? 'System roles cannot be edited' : 'Edit role'}
                      >
                        <span className="icon icon--edit icon--default"></span>
                      </button>
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
            {filteredPermissions.map(permission => (
              <tr key={permission.id} className="material-tr">
                <td className="material-cell permission-info">
                  <div className="permission-details">
                    <span className="category-icon">{permission.categoryIcon}</span>
                    <div>
                      <div className="permission-label">{permission.label}</div>
                      <div className="permission-category">{permission.category}</div>
                      <div className="permission-description">{permission.description}</div>
                    </div>
                  </div>
                </td>
                {roles.map(role => (
                  <td key={role.id} className="material-cell permission-cell">
                    <div
                      className={`permission-status ${hasPermission(role, permission.id) ? 'granted' : 'denied'}`}
                      title={`${permission.label} is ${hasPermission(role, permission.id) ? 'granted' : 'denied'} for ${role.name}`}
                    >
                      {hasPermission(role, permission.id) ? '✓' : '✗'}
                    </div>
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
};

// Audit Log Component
const AuditLog = ({ auditLog, users, roles }) => {
  const getUser = (userId) => users.find(u => u.id === userId);
  const getRole = (roleId) => roles.find(r => r.id === roleId);
  
  const getActionIcon = (action) => {
    switch (action) {
      case 'role_created': return <span className="icon icon--add icon--success"></span>;
      case 'role_modified': return <span className="icon icon--edit icon--default"></span>;
      case 'role_deleted': return '🗑️';
      case 'role_assigned': return '👤';
      case 'role_unassigned': return '👤';
      default: return '📝';
    }
  };

  const getActionColor = (action) => {
    switch (action) {
      case 'role_created': return '#10b981';
      case 'role_modified': return '#f59e0b';
      case 'role_deleted': return '#ef4444';
      case 'role_assigned': return '#3b82f6';
      case 'role_unassigned': return '#6b7280';
      default: return '#6b7280';
    }
  };

  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return {
      date: date.toLocaleDateString(),
      time: date.toLocaleTimeString()
    };
  };

  return (
    <div className="audit-log">
      <div className="audit-header">
        <h4>Role Changes Audit Log</h4>
        <p>Track all changes to roles and permissions</p>
      </div>

      <div className="audit-timeline">
        {auditLog.length === 0 ? (
          <div className="no-audit-entries">
            <p>No audit entries found</p>
          </div>
        ) : (
          auditLog.map(entry => {
            const performedByUser = getUser(entry.performedBy);
            const affectedUser = entry.affectedUser ? getUser(entry.affectedUser) : null;
            const role = entry.roleId ? getRole(entry.roleId) : null;
            const { date, time } = formatTimestamp(entry.performedAt);
            
            return (
              <div key={entry.id} className="audit-entry">
                <div className="audit-icon">
                  <span 
                    className="action-icon"
                    style={{ color: getActionColor(entry.action) }}
                  >
                    {getActionIcon(entry.action)}
                  </span>
                </div>
                
                <div className="audit-content">
                  <div className="audit-header-info">
                    <div className="audit-details">
                      <strong>{entry.details}</strong>
                      {role && (
                        <div className="role-reference">
                          <div 
                            className="role-color-indicator-xs" 
                            style={{ backgroundColor: role.color }}
                          ></div>
                          <span>{role.name}</span>
                        </div>
                      )}
                    </div>
                    <div className="audit-timestamp">
                      <div className="audit-date">{date}</div>
                      <div className="audit-time">{time}</div>
                    </div>
                  </div>
                  
                  <div className="audit-metadata">
                    <div className="performed-by">
                      Performed by: <strong>{performedByUser?.name || 'System'}</strong>
                    </div>
                    {affectedUser && (
                      <div className="affected-user">
                        Affected user: <strong>{affectedUser.name}</strong> ({affectedUser.email})
                      </div>
                    )}
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RolesPermissionsView;